import React from "react";
import { DOC_TYPES, LOCALSTORAGE_ITEMS, TYPES } from "../../../util/Constants";
import * as StorageService from "../../../services/StorageServices";
import { FormattedDate } from "react-intl";
import { PATH } from "../../../routes/routes";
import ClientFormBonDeComandaDetails from "../ClientFormBonDeComandaDetails";
import {
  getAllClientFormsHandler,
  getMyClientFormsHandler,
  deleteClientFormByIdHandler,
  exportClientFormsExcelHandler,
} from "../../../handlers/clientFormsHandlers/HandlerClientForms";
import StatusBadge from "../../../components/StatusBadge";
import { PAGE_SIZE } from "../../../config/ClientConfig";

const BON_COMANDA_PREFIX = "BON_COMANDA_";

const getCompanyInfo = () => {
  const raw = StorageService.getItem(LOCALSTORAGE_ITEMS.COMPANY_INFO);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch(err) {
    console.error("getCompanyInfo", err);
    return null;
  }
};

const getAccountType = () => {
  return StorageService.getItem(LOCALSTORAGE_ITEMS.TYPE) || "Unknown";
};

const resolveBonComandaFormId = () => {
  const company = getCompanyInfo();
  const list = company?.availableClientForms || [];
  return list.find((x) => typeof x === "string" && x.startsWith(BON_COMANDA_PREFIX)) || null;
};

const FIELD_KEYS = {
  client: "client",
  telefon: "telefon",

  produsReceptionat: "produsReceptionat",
  produsAltulText: "produsAltulText",

  operatiuneSolicitata: "operatiuneSolicitata",
  operatiuneAltulText: "operatiuneAltulText",

  pretEstimat: "pretEstimat",
};

const getFieldArr = (fields, key) => {
  const f = (fields || []).find((x) => x?.id === key || x?.name === key);
  if (!f) return [];
  return Array.isArray(f.value) ? f.value : [];
};

export const clientFormBonDeComandaConfig = ({ companyId }) => {
  const type = getAccountType();
  const isAdmin = type === TYPES.ADMIN;

  const columnsBase = [
    {
      Header: "#",
      accessor: "rowNo",
      isSortable: false,
      disableFilters: true,
      filterable: false,
      Cell: (row) => row.value,
    },

    { Header: "Client", accessor: "clientName", isSortable: false, hasIcon: true },
    { Header: "Telefon", accessor: "clientPhone", isSortable: false },
    { Header: "Locație", accessor: "locationName", isSortable: false },

    {
      Header: "Data creării",
      accessor: "created",
      isSortable: true,
      disableFilters: true,
      Cell: (row) => (
        <FormattedDate
          value={row.value}
          month="short"
          year="numeric"
          hour="numeric"
          minute="numeric"
          hour12={false}
          day="numeric"
        />
      ),
    },

    {
      Header: "Status",
      accessor: "status",
      isSortable: false,
      Cell: (row) => <StatusBadge status={row.value} />,
    },

    { Header: "Chitanță", accessor: "receipt", isSortable: false },
  ];

  const columns = isAdmin
    ? [...columnsBase, { Header: "Actions", accessor: "actions", isSortable: false, filterable: false }]
    : columnsBase;

  return {
    path: PATH.SERVICE_BONURI ? PATH.SERVICE_BONURI(companyId) : PATH.SERVICE(companyId) + "/bonuri",
    label: "Bon de comandă",
    value: DOC_TYPES.BON,
    headerTitle: "Bonuri de comandă",
    addBtnText: "Adaugă",
    pageSize: PAGE_SIZE,
    DetailsComponent: ClientFormBonDeComandaDetails,

    getAll: async ({ createdFrom, createdTo, page = 0, sort, locationsIds } = {}) => {
      const formId = resolveBonComandaFormId();

      if (!formId) {
        return {
          data: {
            content: [],
            page: 0,
            size: PAGE_SIZE,
            totalElements: 0,
            totalPages: 0,
            first: true,
            last: true,
            empty: true,
          },
        };
      }

      const params = { companyId, formId, page, size: PAGE_SIZE, sort: sort || ["created,DESC"] };
      if (createdFrom) params.createdFrom = createdFrom;
      if (createdTo) params.createdTo = createdTo;
      if (locationsIds && locationsIds.length) params.locationsIds = locationsIds;

      return type === TYPES.WORKER
        ? getMyClientFormsHandler(params)
        : getAllClientFormsHandler(params);
    },

    del: (id) => deleteClientFormByIdHandler(id),

    exportFileName: "bonuri-comanda",
    exportExcel: async ({ createdFrom, createdTo, sort, locationsIds, fileName }) => {
      const formId = resolveBonComandaFormId();
      if (!formId) return { data: { error: "Formular indisponibil" } };

      const params = { companyId, formId, sort: sort || ["created,DESC"] };
      if (createdFrom) params.createdFrom = createdFrom;
      if (createdTo) params.createdTo = createdTo;
      if (locationsIds && locationsIds.length) params.locationsIds = locationsIds;

      return exportClientFormsExcelHandler(fileName, params);
    },

    columns,

    mapList: (items) =>
      (items || []).map((cf, idx) => {
        const fields = cf?.fields || [];

        const client = (getFieldArr(fields, FIELD_KEYS.client)[0] || "").trim();
        const telefon = (getFieldArr(fields, FIELD_KEYS.telefon)[0] || "").trim();

        const produsKey = (getFieldArr(fields, FIELD_KEYS.produsReceptionat)[0] || "").trim();
        const produsAlt = (getFieldArr(fields, FIELD_KEYS.produsAltulText)[0] || "").trim();
        const produs = produsKey === "altul" ? produsAlt || "-" : produsKey || "-";

        const operValues = getFieldArr(fields, FIELD_KEYS.operatiuneSolicitata) || [];
        const operAlt = (getFieldArr(fields, FIELD_KEYS.operatiuneAltulText)[0] || "").trim();
        const operatiuni = [...operValues, operAlt].filter(Boolean).join(", ");

        const pretEstimatLei = (getFieldArr(fields, FIELD_KEYS.pretEstimat)[0] || "").trim();

        return {
          ...cf,
          rowNo: idx + 1,
          status: (cf?.status || "PENDING").toUpperCase(),
          locationId: String(cf?.location?.id || ""),
          locationName: cf?.location?.name || "-",
          location: cf?.location || null,
          client,
          telefon,
          produs,
          operatiuni,
          pretEstimatLei,
          created: cf?.created || new Date(),
          receipt: String(cf?.receipt || ""),
        };
      }),

    pickListFromResponse: (data) => data?.items || data?.content || data?.page?.content || [],

    deleteAlertTitle: "Ștergere bon de comandă",
    deleteAlertText: "Ești sigur că vrei să ștergi bonul de comandă?",
    getDeleteLabel: (row) => (row?.client ? `${row.client} (${row.telefon || "-"})` : row?.id),
  };
};